import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Loader2, Tag, Upload, Trash2 } from 'lucide-react';
import { notifyError, notifySuccess } from '../lib/notify';
import { LOCAL_DEAL_CATEGORIES } from '../constants/localDeals';

const API = process.env.REACT_APP_BACKEND_URL || '';
const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'des9fk8et';
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'frg_profile_photos';

const labelCls =
  'font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 mb-1.5 block';
const inputCls =
  'w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/25 transition';

const loadCloudinaryScript = () =>
  new Promise((resolve) => {
    if (window.cloudinary) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://upload-widget.cloudinary.com/latest/global/all.js';
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });

/**
 * Member-facing Local Deal submission form. Submissions land in
 * db.local_deal_submissions with status="pending" until admin approval.
 *
 * Survey consent checkbox is REQUIRED per Iter 71 compliance.
 */
const SubmitLocalDealModal = ({ open, onClose, onSubmitted }) => {
  const [form, setForm] = useState({
    name: '',
    city: '',
    category: '',
    description: '',
    discount: '',
    valid_until: '',
    url: '',
    image_url: '',
    survey_consent: false,
  });
  const [cities, setCities] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/api/cities`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        const items = Array.isArray(d) ? d : d.cities || [];
        setCities(items.map((c) => c.name).filter(Boolean).sort());
      })
      .catch(() => {});
    loadCloudinaryScript();
  }, [open]);

  if (!open) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openUploadWidget = () => {
    if (!window.cloudinary || !CLOUD_NAME || !UPLOAD_PRESET) {
      notifyError('Photo upload is not configured.');
      return;
    }
    setUploading(true);
    const w = window.cloudinary.createUploadWidget(
      {
        cloudName: CLOUD_NAME,
        uploadPreset: UPLOAD_PRESET,
        sources: ['local', 'url'],
        multiple: false,
        resourceType: 'image',
        folder: 'mundalo_local_deal_subs',
        maxFileSize: 8_000_000,
        cropping: false,
      },
      (err, result) => {
        if (err) {
          setUploading(false);
          notifyError('Logo upload failed.');
          return;
        }
        if (result && result.event === 'success') {
          set('image_url', result.info.secure_url);
        }
        if (result && (result.event === 'close' || result.event === 'queues-end')) {
          setUploading(false);
        }
      },
    );
    w.open();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return notifyError('Add the business name.');
    if (!form.city) return notifyError('Pick a city.');
    if (!form.category) return notifyError('Pick a category.');
    if (!form.description.trim() || form.description.trim().length < 10)
      return notifyError('Description must be at least 10 characters.');
    if (!form.discount.trim()) return notifyError("What's the discount or offer?");
    if (!form.valid_until) return notifyError('Pick a valid-until date.');
    if (!form.survey_consent)
      return notifyError('Please agree to receive the follow-up survey to submit.');

    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/local-deals/submit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          city: form.city,
          category: form.category,
          description: form.description.trim(),
          discount: form.discount.trim(),
          valid_until: form.valid_until,
          url: form.url.trim(),
          image_url: form.image_url || null,
          survey_consent: form.survey_consent,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        notifyError(d.detail || 'Could not submit your deal. Please try again.');
        setSubmitting(false);
        return;
      }
      notifySuccess('Your deal has been submitted for review.');
      if (onSubmitted) onSubmitted();
      onClose();
    } catch {
      notifyError('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A1F2E]/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="submit-deal-modal"
      onClick={onClose}
    >
      <div
        className="bg-[#F9F6EF] border border-[#EFE6CF] rounded-2xl max-w-2xl w-full my-8 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#EFE6CF]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#F4ECD8] flex items-center justify-center">
              <Tag className="w-5 h-5 text-[#1A5C6E]" />
            </div>
            <div>
              <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#1A5C6E]">
                Submit a Local Deal
              </p>
              <h2 className="font-display italic text-2xl text-[#0A1F2E]">
                Share a discount.
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#0A1F2E]/55 hover:text-[#0A1F2E] transition"
            data-testid="submit-deal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <p className="text-sm text-[#0A1F2E]/70 bg-[#F4ECD8] border border-[#EFE6CF] rounded-xl px-4 py-3">
            Submissions are reviewed by the Mundalo team before going live. You'll
            be asked to confirm a brief follow-up survey about the deal.
          </p>

          <div>
            <label className={labelCls}>Business name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Café Negro Mérida"
              className={inputCls}
              data-testid="deal-form-name"
              maxLength={160}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>City</label>
              <select
                value={form.city}
                onChange={(e) => set('city', e.target.value)}
                className={inputCls}
                data-testid="deal-form-city"
              >
                <option value="">Pick a city…</option>
                {cities.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select
                value={form.category}
                onChange={(e) => set('category', e.target.value)}
                className={inputCls}
                data-testid="deal-form-category"
              >
                <option value="">Pick a category…</option>
                {LOCAL_DEAL_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>Description</label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="What's the vibe? Why should Mundalo members care?"
              rows={3}
              className={inputCls}
              data-testid="deal-form-description"
              maxLength={2000}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Discount or offer</label>
              <input
                type="text"
                value={form.discount}
                onChange={(e) => set('discount', e.target.value)}
                placeholder="e.g. 15% off any meal with code MUNDALO"
                className={inputCls}
                data-testid="deal-form-discount"
                maxLength={200}
              />
            </div>
            <div>
              <label className={labelCls}>Valid until</label>
              <input
                type="date"
                value={form.valid_until}
                onChange={(e) => set('valid_until', e.target.value)}
                className={inputCls}
                data-testid="deal-form-valid-until"
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>Website URL (optional)</label>
            <input
              type="url"
              value={form.url}
              onChange={(e) => set('url', e.target.value)}
              placeholder="https://example.com"
              className={inputCls}
              data-testid="deal-form-url"
            />
          </div>

          <div>
            <label className={labelCls}>Logo / photo (optional)</label>
            <div className="flex items-center gap-3">
              {form.image_url ? (
                <div className="relative group w-20 h-20">
                  <img
                    src={form.image_url}
                    alt="Logo preview"
                    className="w-20 h-20 object-cover rounded-lg border border-[#EFE6CF]"
                  />
                  <button
                    type="button"
                    onClick={() => set('image_url', '')}
                    className="absolute top-1 right-1 bg-[#0A1F2E]/70 hover:bg-[#B91C1C] text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                    data-testid="deal-form-remove-photo"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={openUploadWidget}
                  disabled={uploading}
                  className="w-20 h-20 rounded-lg border-2 border-dashed border-[#EFE6CF] hover:border-[#2CA8A0]/60 flex flex-col items-center justify-center text-[#0A1F2E]/55 text-[10px] gap-1 transition disabled:opacity-50"
                  data-testid="deal-form-upload-btn"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload
                    </>
                  )}
                </button>
              )}
              <p className="text-[10px] text-[#0A1F2E]/55 max-w-[14rem]">
                Square works best. Up to 8 MB.
              </p>
            </div>
          </div>

          {/* Iter 71 - required survey consent */}
          <label
            className="flex items-start gap-3 text-[13px] text-[#0A1F2E]/85 leading-snug cursor-pointer pt-2 border-t border-[#EFE6CF] -mx-6 px-6 pt-4"
            data-testid="deal-form-survey-consent-row"
          >
            <input
              type="checkbox"
              required
              checked={form.survey_consent}
              onChange={(e) => set('survey_consent', e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-[#0A1F2E]/30 text-[#2CA8A0] focus:ring-[#2CA8A0]/40 flex-shrink-0"
              data-testid="deal-form-survey-consent"
            />
            <span>
              I agree to receive a follow-up survey about my experience with this
              deal. (Required to submit.) See our{' '}
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#1A5C6E] underline hover:text-[#0A1F2E]"
              >
                Terms of Service
              </Link>.
            </span>
          </label>

          <div className="flex justify-end gap-2 pt-3 border-t border-[#EFE6CF]">
            <button
              type="button"
              onClick={onClose}
              className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#0A1F2E]/60 hover:text-[#0A1F2E] px-4 py-2 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#163448] text-white rounded-full px-5 py-2.5 font-mono uppercase text-[11px] tracking-[0.15em] transition disabled:opacity-50"
              data-testid="deal-form-submit"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Submit for review
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubmitLocalDealModal;
